<template>
  <component :is="embedded ? 'div' : 'v-container'" :fluid="!embedded" :class="embedded ? 'intelligence-succession' : 'intelligence-succession pa-4'">
    <v-row>
      <v-col cols="12">
        <h1 class="text-h5 font-weight-bold mb-1 theme-heading">
          Succession Planning
        </h1>
        <p class="text-subtitle-2 grey--text text--darken-1 mb-4">
          Identify internal candidates for critical roles by readiness tier
        </p>
      </v-col>
    </v-row>

    <v-alert v-if="hasError" type="error" dismissible class="mb-4">
      {{ errorMessage }}
    </v-alert>

    <v-row>
      <v-col cols="12" md="4">
        <v-card outlined class="pa-4">
          <v-btn-toggle
            v-model="currentMode"
            mandatory
            dense
            color="#007AFF"
            class="mb-3"
            style="width: 100%"
          >
            <v-btn value="role" small style="flex: 1">
              <v-icon small left>mdi-briefcase-outline</v-icon>
              By Role
            </v-btn>
            <v-btn value="person" small style="flex: 1">
              <v-icon small left>mdi-account-outline</v-icon>
              By Person
            </v-btn>
          </v-btn-toggle>

          <template v-if="currentMode === 'role'">
            <h3 class="text-subtitle-2 font-weight-bold mb-3 theme-heading">
              Select Critical Role
            </h3>

            <v-list dense style="max-height: 280px; overflow-y: auto">
              <v-list-item
                v-for="role in presetRoles"
                :key="role.id"
                :class="{ 'v-list-item--active': selectedRole === role.title }"
                @click="selectRole(role)"
                class="role-item"
              >
                <v-list-item-content>
                  <v-list-item-title class="text-body-2">{{ role.title }}</v-list-item-title>
                  <v-list-item-subtitle>{{ role.department }}{{ role.count ? ' (' + role.count + ')' : '' }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-icon small v-if="selectedRole === role.title" color="#007AFF">mdi-check</v-icon>
                </v-list-item-action>
              </v-list-item>
            </v-list>

            <v-divider class="my-3" />

            <v-switch
              v-model="useCustomRole"
              label="Custom role"
              color="#007AFF"
              dense
              hide-details
              class="mb-2"
            />

            <v-text-field
              v-if="useCustomRole"
              v-model="customRole"
              label="Enter role title..."
              outlined
              dense
              color="#007AFF"
              class="mb-3"
            />

            <v-text-field
              v-model="department"
              label="Department (optional)"
              outlined
              dense
              color="#007AFF"
              class="mb-3"
            />
          </template>

          <template v-else>
            <h3 class="text-subtitle-2 font-weight-bold mb-3 theme-heading">
              Find Successors For
            </h3>

            <v-autocomplete
              v-model="selectedPersonModel"
              :items="personProfiles"
              :search-input.sync="personSearchInput"
              item-text="name"
              return-object
              label="Search employee..."
              outlined
              dense
              color="#007AFF"
              hide-no-data
              class="mb-3"
              @focus="loadPersonProfiles('')"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ item.currentTitle || item.title }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>

            <v-card v-if="selectedPerson" flat class="pa-3 mb-3 theme-surface">
              <div class="text-body-2 font-weight-bold theme-heading">
                {{ selectedPerson.name }}
              </div>
              <div class="text-caption grey--text">
                {{ selectedPerson.currentTitle || selectedPerson.title }}
              </div>
              <div class="text-caption grey--text">
                {{ selectedPerson.functionalArea || selectedPerson.department || "" }}
              </div>
            </v-card>
          </template>

          <v-btn
            block
            color="#007AFF"
            dark
            :loading="isLoading"
            :disabled="currentMode === 'person' ? !selectedPerson : !activeRole"
            @click="search"
          >
            <v-icon left>mdi-account-switch</v-icon>
            {{ currentMode === 'person' && selectedPerson ? 'Find Successors for ' + selectedPerson.name : 'Find Succession Candidates' }}
          </v-btn>
        </v-card>
      </v-col>

      <v-col cols="12" md="8">
        <div v-if="!hasCandidates && !isLoading">
          <v-card outlined class="pa-6 text-center">
            <v-icon size="64" color="#E0E0E0" class="mb-3">mdi-account-switch-outline</v-icon>
            <h3 class="text-subtitle-1 font-weight-bold mb-2 theme-heading">
              How Succession Planning Works
            </h3>
            <div class="text-body-2 grey--text text--darken-1 mb-4" style="max-width: 420px; margin: 0 auto">
              Select a critical role or choose a specific person, and our deterministic engine will scan all
              indexed profiles, score them based on skills, experience depth, and
              STAR evidence, then categorize candidates by readiness tier.
            </div>
          </v-card>
        </div>

        <div v-if="hasCandidates">
          <div v-if="currentMode === 'person' && targetPerson" class="mb-3">
            <div class="text-subtitle-2 theme-heading">
              <v-icon small color="#007AFF" class="mr-1">mdi-account-switch</v-icon>
              Who could succeed <strong>{{ targetPerson.name }}</strong> based on demonstrated competencies and career evidence
            </div>
            <div class="text-caption grey--text mt-1">
              {{ targetPerson.title }}<span v-if="targetPerson.seniority"> &middot; {{ targetPerson.seniority }}</span>
            </div>
          </div>
          <div v-else-if="currentMode === 'person' && selectedPerson" class="text-subtitle-2 mb-3 theme-heading">
            <v-icon small color="#007AFF" class="mr-1">mdi-account-switch</v-icon>
            Who could succeed <strong>{{ selectedPerson.name }}</strong> based on demonstrated competencies and career evidence
          </div>

          <v-row dense class="mb-4">
            <v-col cols="4">
              <v-card outlined class="pa-3 text-center" style="border-left: 4px solid #10B981">
                <div class="text-h4 font-weight-bold" style="color: #10B981">
                  {{ readyNow.length }}
                </div>
                <div class="text-caption grey--text">Ready Now</div>
              </v-card>
            </v-col>
            <v-col cols="4">
              <v-card outlined class="pa-3 text-center" style="border-left: 4px solid #F59E0B">
                <div class="text-h4 font-weight-bold" style="color: #F59E0B">
                  {{ readySoon.length }}
                </div>
                <div class="text-caption grey--text">Ready 1-2 Years</div>
              </v-card>
            </v-col>
            <v-col cols="4">
              <v-card outlined class="pa-3 text-center" style="border-left: 4px solid #9E9E9E">
                <div class="text-h4 font-weight-bold" style="color: #9E9E9E">
                  {{ developmental.length }}
                </div>
                <div class="text-caption grey--text">Developmental</div>
              </v-card>
            </v-col>
          </v-row>

          <div v-if="compareSelection.length >= 2" class="mb-4">
            <v-card outlined class="pa-4">
              <div class="d-flex align-center mb-3">
                <div class="text-subtitle-2 font-weight-bold theme-heading flex-grow-1">
                  <v-icon small color="#007AFF" class="mr-1">mdi-compare</v-icon>
                  Candidate Comparison
                </div>
                <span class="text-caption grey--text mr-3">Selected: {{ compareSelection.length }}/3</span>
                <v-btn x-small outlined color="#007AFF" @click="clearCompareSelection">Clear Selection</v-btn>
              </div>
              <div style="overflow-x: auto">
                <table class="compare-table">
                  <thead>
                    <tr>
                      <th class="text-left text-caption font-weight-bold" style="min-width: 140px">Skill</th>
                      <th v-for="cId in compareSelection" :key="'h-'+cId" class="text-center text-caption font-weight-bold" style="min-width: 120px">
                        <template v-if="compareCandidateMap[cId]">
                          {{ compareCandidateMap[cId].name }}<br>
                          <span class="font-weight-regular grey--text">{{ compareCandidateMap[cId].title }}</span>
                        </template>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td class="text-body-2 font-weight-bold">Overall Readiness</td>
                      <td v-for="cId in compareSelection" :key="'o-'+cId" class="text-center">
                        <v-chip
                          v-if="compareCandidateMap[cId]"
                          small
                          :color="scoreColor(compareCandidateMap[cId].readinessScore)"
                          dark
                        >{{ compareCandidateMap[cId].readinessScore }}%</v-chip>
                      </td>
                    </tr>
                    <tr v-for="req in requirementsUsed" :key="'r-'+req">
                      <td class="text-body-2">{{ req }}</td>
                      <td v-for="cId in compareSelection" :key="'s-'+cId+'-'+req" class="text-center">
                        <v-chip
                          v-if="compareCandidateMap[cId]"
                          x-small
                          :color="scoreColor(getSkillScore(compareCandidateMap[cId], req))"
                          dark
                        >{{ getSkillScore(compareCandidateMap[cId], req) }}%</v-chip>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </v-card>
          </div>

          <div v-if="readyNow.length > 0" class="mb-4">
            <div class="text-subtitle-2 font-weight-bold mb-2" style="color: #10B981">
              <v-icon small color="#10B981" class="mr-1">mdi-check-circle</v-icon>
              Ready Now
            </div>
            <v-card
              v-for="c in readyNow"
              :key="c.profileId"
              outlined
              class="candidate-card mb-2"
            >
              <v-card-text class="py-3 px-4">
                <div class="d-flex align-center">
                  <v-checkbox
                    :input-value="compareSelection.includes(c.profileId)"
                    @change="toggleCompareCandidate(c.profileId)"
                    hide-details
                    dense
                    class="mt-0 mr-2 compare-checkbox"
                    :disabled="!compareSelection.includes(c.profileId) && compareSelection.length >= 3"
                  />
                  <div class="flex-grow-1">
                    <div class="d-flex align-center">
                      <span class="text-body-2 font-weight-bold theme-heading">{{ c.name }}</span>
                      <v-chip v-if="c.seniority" x-small class="ml-2" color="#E3F2FD" text-color="#007AFF">{{ c.seniority }}</v-chip>
                      <v-chip x-small class="ml-1" color="#D1FAE5" text-color="#10B981">Ready Now</v-chip>
                    </div>
                    <div class="text-caption grey--text">{{ c.title }} {{ c.company ? 'at ' + c.company : '' }}</div>
                    <div class="text-caption mt-1" style="color: #6B7280">
                      <span v-if="c.strengths && c.strengths.length">Strengths: {{ c.strengths.slice(0, 3).join(', ') }}</span>
                      <span v-if="c.strengths && c.strengths.length && c.gapAreas && c.gapAreas.length"> | </span>
                      <span v-if="c.gapAreas && c.gapAreas.length">Growth: {{ c.gapAreas.length }} areas</span>
                    </div>
                  </div>
                  <div class="text-center mx-3">
                    <div class="text-h5 font-weight-bold" style="color: #10B981">{{ c.readinessScore }}%</div>
                    <div class="text-caption grey--text">match</div>
                  </div>
                  <div class="d-flex flex-column">
                    <v-btn small text color="#007AFF" @click="viewProfile(c)">
                      <v-icon small left>mdi-account-eye</v-icon>
                      Profile
                    </v-btn>
                    <v-btn small text color="#0D9488" @click="deepDive(c)">
                      <v-icon small left>mdi-chat-processing</v-icon>
                      Deep Dive
                    </v-btn>
                    <v-btn small text color="#8B5CF6" @click="prepInterview(c)">
                      <v-icon small left>mdi-head-question</v-icon>
                      Prep
                    </v-btn>
                  </div>
                </div>
                <div class="mt-2">
                  <v-btn
                    x-small
                    text
                    color="#007AFF"
                    @click="toggleSkillPanel(c.profileId)"
                  >
                    <v-icon x-small left>{{ expandedSkills[c.profileId] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                    Skills Assessment ({{ (c.skillAssessments || []).length }})
                  </v-btn>
                  <div v-if="expandedSkills[c.profileId]" class="mt-2">
                    <div
                      v-for="sa in c.skillAssessments"
                      :key="sa.skill"
                      class="mb-3"
                    >
                      <div class="d-flex align-center mb-1">
                        <span class="text-body-2 font-weight-medium">{{ sa.skill }}</span>
                        <v-chip
                          x-small
                          class="ml-2"
                          :color="criticalityColor(sa.criticality)"
                          :text-color="criticalityTextColor(sa.criticality)"
                        >{{ sa.criticality }}</v-chip>
                        <v-spacer />
                        <span class="text-body-2 font-weight-bold">{{ sa.score }}%</span>
                      </div>
                      <v-progress-linear
                        :value="sa.score"
                        :color="scoreColor(sa.score)"
                        height="6"
                        rounded
                      />
                      <div v-if="sa.evidence" class="text-caption grey--text mt-1" style="line-height: 1.3">{{ sa.evidence }}</div>
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </div>

          <div v-if="readySoon.length > 0" class="mb-4">
            <div class="text-subtitle-2 font-weight-bold mb-2" style="color: #F59E0B">
              <v-icon small color="#F59E0B" class="mr-1">mdi-clock-outline</v-icon>
              Ready in 1-2 Years
            </div>
            <v-card
              v-for="c in readySoon"
              :key="c.profileId"
              outlined
              class="candidate-card mb-2"
            >
              <v-card-text class="py-3 px-4">
                <div class="d-flex align-center">
                  <v-checkbox
                    :input-value="compareSelection.includes(c.profileId)"
                    @change="toggleCompareCandidate(c.profileId)"
                    hide-details
                    dense
                    class="mt-0 mr-2 compare-checkbox"
                    :disabled="!compareSelection.includes(c.profileId) && compareSelection.length >= 3"
                  />
                  <div class="flex-grow-1">
                    <div class="d-flex align-center">
                      <span class="text-body-2 font-weight-bold theme-heading">{{ c.name }}</span>
                      <v-chip v-if="c.seniority" x-small class="ml-2" color="#E3F2FD" text-color="#007AFF">{{ c.seniority }}</v-chip>
                      <v-chip x-small class="ml-1" color="#FEF3C7" text-color="#D97706">1-2 Years</v-chip>
                    </div>
                    <div class="text-caption grey--text">{{ c.title }} {{ c.company ? 'at ' + c.company : '' }}</div>
                    <div class="text-caption mt-1" style="color: #6B7280">
                      <span v-if="c.strengths && c.strengths.length">Strengths: {{ c.strengths.slice(0, 3).join(', ') }}</span>
                      <span v-if="c.strengths && c.strengths.length && c.gapAreas && c.gapAreas.length"> | </span>
                      <span v-if="c.gapAreas && c.gapAreas.length">Growth: {{ c.gapAreas.length }} areas</span>
                    </div>
                  </div>
                  <div class="text-center mx-3">
                    <div class="text-h5 font-weight-bold" style="color: #F59E0B">{{ c.readinessScore }}%</div>
                    <div class="text-caption grey--text">match</div>
                  </div>
                  <div class="d-flex flex-column">
                    <v-btn small text color="#007AFF" @click="viewProfile(c)">
                      <v-icon small left>mdi-account-eye</v-icon>
                      Profile
                    </v-btn>
                    <v-btn small text color="#0D9488" @click="deepDive(c)">
                      <v-icon small left>mdi-chat-processing</v-icon>
                      Deep Dive
                    </v-btn>
                    <v-btn small text color="#8B5CF6" @click="prepInterview(c)">
                      <v-icon small left>mdi-head-question</v-icon>
                      Prep
                    </v-btn>
                  </div>
                </div>
                <div class="mt-2">
                  <v-btn
                    x-small
                    text
                    color="#007AFF"
                    @click="toggleSkillPanel(c.profileId)"
                  >
                    <v-icon x-small left>{{ expandedSkills[c.profileId] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                    Skills Assessment ({{ (c.skillAssessments || []).length }})
                  </v-btn>
                  <div v-if="expandedSkills[c.profileId]" class="mt-2">
                    <div
                      v-for="sa in c.skillAssessments"
                      :key="sa.skill"
                      class="mb-3"
                    >
                      <div class="d-flex align-center mb-1">
                        <span class="text-body-2 font-weight-medium">{{ sa.skill }}</span>
                        <v-chip
                          x-small
                          class="ml-2"
                          :color="criticalityColor(sa.criticality)"
                          :text-color="criticalityTextColor(sa.criticality)"
                        >{{ sa.criticality }}</v-chip>
                        <v-spacer />
                        <span class="text-body-2 font-weight-bold">{{ sa.score }}%</span>
                      </div>
                      <v-progress-linear
                        :value="sa.score"
                        :color="scoreColor(sa.score)"
                        height="6"
                        rounded
                      />
                      <div v-if="sa.evidence" class="text-caption grey--text mt-1" style="line-height: 1.3">{{ sa.evidence }}</div>
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </div>

          <div v-if="developmental.length > 0" class="mb-4">
            <div class="text-subtitle-2 font-weight-bold mb-2 grey--text text--darken-1">
              <v-icon small color="#9E9E9E" class="mr-1">mdi-school-outline</v-icon>
              Developmental
            </div>
            <v-card
              v-for="c in developmental"
              :key="c.profileId"
              outlined
              class="candidate-card mb-2"
            >
              <v-card-text class="py-3 px-4">
                <div class="d-flex align-center">
                  <v-checkbox
                    :input-value="compareSelection.includes(c.profileId)"
                    @change="toggleCompareCandidate(c.profileId)"
                    hide-details
                    dense
                    class="mt-0 mr-2 compare-checkbox"
                    :disabled="!compareSelection.includes(c.profileId) && compareSelection.length >= 3"
                  />
                  <div class="flex-grow-1">
                    <div class="d-flex align-center">
                      <span class="text-body-2 font-weight-bold theme-heading">{{ c.name }}</span>
                      <v-chip v-if="c.seniority" x-small class="ml-2" color="#E3F2FD" text-color="#007AFF">{{ c.seniority }}</v-chip>
                      <v-chip x-small class="ml-1" color="#F3F4F6" text-color="#6B7280">Developmental</v-chip>
                    </div>
                    <div class="text-caption grey--text">{{ c.title }} {{ c.company ? 'at ' + c.company : '' }}</div>
                    <div class="text-caption mt-1" style="color: #6B7280">
                      <span v-if="c.strengths && c.strengths.length">Strengths: {{ c.strengths.slice(0, 3).join(', ') }}</span>
                      <span v-if="c.strengths && c.strengths.length && c.gapAreas && c.gapAreas.length"> | </span>
                      <span v-if="c.gapAreas && c.gapAreas.length">Growth: {{ c.gapAreas.length }} areas</span>
                    </div>
                  </div>
                  <div class="text-center mx-3">
                    <div class="text-h5 font-weight-bold" style="color: #9E9E9E">{{ c.readinessScore }}%</div>
                    <div class="text-caption grey--text">match</div>
                  </div>
                  <v-btn small text color="#007AFF" @click="viewProfile(c)">
                    <v-icon small left>mdi-account-eye</v-icon>
                    Profile
                  </v-btn>
                </div>
                <div class="mt-2">
                  <v-btn
                    x-small
                    text
                    color="#007AFF"
                    @click="toggleSkillPanel(c.profileId)"
                  >
                    <v-icon x-small left>{{ expandedSkills[c.profileId] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                    Skills Assessment ({{ (c.skillAssessments || []).length }})
                  </v-btn>
                  <div v-if="expandedSkills[c.profileId]" class="mt-2">
                    <div
                      v-for="sa in c.skillAssessments"
                      :key="sa.skill"
                      class="mb-3"
                    >
                      <div class="d-flex align-center mb-1">
                        <span class="text-body-2 font-weight-medium">{{ sa.skill }}</span>
                        <v-chip
                          x-small
                          class="ml-2"
                          :color="criticalityColor(sa.criticality)"
                          :text-color="criticalityTextColor(sa.criticality)"
                        >{{ sa.criticality }}</v-chip>
                        <v-spacer />
                        <span class="text-body-2 font-weight-bold">{{ sa.score }}%</span>
                      </div>
                      <v-progress-linear
                        :value="sa.score"
                        :color="scoreColor(sa.score)"
                        height="6"
                        rounded
                      />
                      <div v-if="sa.evidence" class="text-caption grey--text mt-1" style="line-height: 1.3">{{ sa.evidence }}</div>
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </div>
        </div>

        <v-progress-linear v-if="isLoading" indeterminate color="#007AFF" />
      </v-col>
    </v-row>
  </component>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";

export default {
  name: "IntelligenceSuccessionPlanning",
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      personSearchInput: "",
      expandedSkills: {},
    };
  },
  computed: {
    ...mapState("intelligenceSuccession", [
      "selectedRole",
      "presetRoles",
      "isLoading",
      "hasError",
      "errorMessage",
      "mode",
      "personProfiles",
      "selectedPerson",
      "targetPerson",
      "requirementsUsed",
      "compareSelection",
    ]),
    ...mapGetters("intelligenceSuccession", [
      "hasCandidates",
      "activeRole",
      "readyNow",
      "readySoon",
      "developmental",
    ]),
    currentMode: {
      get() { return this.$store.state.intelligenceSuccession.mode; },
      set(val) { this.$store.commit("intelligenceSuccession/setMode", val); },
    },
    selectedPersonModel: {
      get() { return this.$store.state.intelligenceSuccession.selectedPerson; },
      set(val) { this.$store.commit("intelligenceSuccession/setSelectedPerson", val); },
    },
    useCustomRole: {
      get() { return this.$store.state.intelligenceSuccession.useCustomRole; },
      set(val) { this.$store.commit("intelligenceSuccession/updateField", { path: "useCustomRole", value: val }); },
    },
    customRole: {
      get() { return this.$store.state.intelligenceSuccession.customRole; },
      set(val) { this.$store.commit("intelligenceSuccession/updateField", { path: "customRole", value: val }); },
    },
    department: {
      get() { return this.$store.state.intelligenceSuccession.department; },
      set(val) { this.$store.commit("intelligenceSuccession/updateField", { path: "department", value: val }); },
    },
    compareCandidateMap() {
      const all = [
        ...(this.readyNow || []),
        ...(this.readySoon || []),
        ...(this.developmental || []),
      ];
      const map = {};
      all.forEach((c) => { map[c.profileId] = c; });
      return map;
    },
  },
  watch: {
    personSearchInput(val) {
      if (this.mode === "person") {
        // Load on every change (including empty) so the dropdown is never empty
        // before the user has typed anything.
        this.loadPersonProfiles(val || "");
      }
    },
    "$route.query.role"(val) {
      if (val) {
        this.applyRoleFromQuery();
      }
    },
    "$route.query.department"() {
      this.applyRoleFromQuery();
    },
  },
  mounted() {
    this.loadDynamicRoles().then(() => {
      this.applyRoleFromQuery();
    });
  },
  methods: {
    ...mapActions("intelligenceSuccession", ["findCandidates", "loadDynamicRoles"]),
    ...mapMutations("intelligenceSuccession", ["selectPresetRole", "toggleCompareCandidate", "clearCompareSelection"]),
    applyRoleFromQuery() {
      const roleParam = this.$route.query.role;
      const deptParam = this.$route.query.department;
      if (!roleParam) return;
      const match = this.presetRoles.find(
        (r) => r.title.toLowerCase() === roleParam.toLowerCase()
      );
      if (match) {
        this.selectPresetRole(match);
      } else {
        this.$store.commit("intelligenceSuccession/updateField", { path: "useCustomRole", value: true });
        this.$store.commit("intelligenceSuccession/updateField", { path: "customRole", value: roleParam });
      }
      if (deptParam) {
        this.$store.commit("intelligenceSuccession/updateField", { path: "department", value: deptParam });
      }
      this.$nextTick(() => {
        this.findCandidates();
      });
    },
    selectRole(role) {
      this.selectPresetRole(role);
    },
    search() {
      this.findCandidates();
    },
    viewProfile(c) {
      this.$router.push({ name: "IntelligenceProfile", params: { profileId: c.profileId }, query: { from: 'succession' } });
    },
    deepDive(c) {
      this.$router.push({ name: "IntelligenceProfile", params: { profileId: c.profileId }, query: { from: 'succession-deepdive' } });
    },
    prepInterview(c) {
      this.$router.push({
        name: "IntelligenceInterviewPrep",
        query: {
          profileId: c.profileId,
          name: c.name,
          role: c.title,
          department: this.department || c.department || "",
        },
      });
    },
    toggleSkillPanel(profileId) {
      this.$set(this.expandedSkills, profileId, !this.expandedSkills[profileId]);
    },
    scoreColor(score) {
      if (score >= 80) return "#10B981";
      if (score >= 60) return "#F59E0B";
      return "#EF4444";
    },
    criticalityColor(criticality) {
      const c = (criticality || "").toLowerCase();
      if (c === "critical") return "#FEE2E2";
      if (c === "high") return "#FEF3C7";
      return "#F3F4F6";
    },
    criticalityTextColor(criticality) {
      const c = (criticality || "").toLowerCase();
      if (c === "critical") return "#DC2626";
      if (c === "high") return "#D97706";
      return "#6B7280";
    },
    getSkillScore(candidate, skillName) {
      if (!candidate || !candidate.skillAssessments) return 0;
      const sa = candidate.skillAssessments.find(
        (s) => s.skill && s.skill.toLowerCase() === skillName.toLowerCase()
      );
      return sa ? sa.score : 0;
    },
    async loadPersonProfiles(search) {
      try {
        const profileStore = this.$store.state.intelligenceProfile;
        const cachedProfiles = (profileStore && profileStore.directory && profileStore.directory.profiles) || [];
        if (cachedProfiles.length > 0) {
          const filtered = cachedProfiles
            .filter((p) => p.name && p.name.toLowerCase().includes(search.toLowerCase()))
            .map((p) => ({
              id: p.id || p.profileId,
              profileId: p.profileId || p.id,
              name: p.name,
              currentTitle: p.currentTitle || p.title || "",
              department: p.functionalArea || p.department || "",
            }))
            .slice(0, 20);
          if (filtered.length > 0) {
            this.$store.commit("intelligenceSuccession/setPersonProfiles", filtered);
            return;
          }
        }
        const { Get } = await import("../../api/ApiBase");
        const result = await Get(`/api/GetProfileAutocomplete?prefix=${encodeURIComponent(search)}`);
        if (result.success && result.data) {
          this.$store.commit("intelligenceSuccession/setPersonProfiles",
            result.data.map((p) => ({
              id: p.id || p.profileId,
              profileId: p.profileId || p.id,
              name: p.name,
              currentTitle: p.currentTitle || p.title || "",
              department: p.department || p.functionalArea || "",
            }))
          );
        }
      } catch (e) {
        // silently fail autocomplete
      }
    },
  },
};
</script>

<style scoped>
.intelligence-succession {
  max-width: 1400px;
}
.candidate-card {
  transition: box-shadow 0.2s ease;
}
.candidate-card:hover {
  box-shadow: 0 2px 8px rgba(30, 58, 95, 0.15);
}
.role-item {
  border-radius: 4px;
  cursor: pointer;
}
.compare-checkbox {
  flex: 0 0 auto;
}
.compare-table {
  width: 100%;
  border-collapse: collapse;
}
.compare-table th,
.compare-table td {
  padding: 6px 10px;
  border-bottom: 1px solid #E5E7EB;
}
.compare-table tr:last-child td {
  border-bottom: none;
}
</style>
